import React, { useState } from 'react';
import styled from 'styled-components';
import AppLogo from './components/AppLogo';
import './App.css';

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 15px;
  margin: 5px 0;
  border-radius: 15px;
  max-width: 80%;
  word-wrap: break-word;
  line-height: 1.4;
  background: ${props => props.$sender === 'user' ? '#4a90e2' : '#f0f0f0'};
  color: ${props => props.$sender === 'user' ? 'white' : '#333'};
  align-self: ${props => props.$sender === 'user' ? 'flex-end' : 'flex-start'};
  text-align: left;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
`;

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin: 10px 0;
  width: 100%;
  justify-content: ${props => props.$sender === 'user' ? 'flex-end' : 'flex-start'};
`;

// API Configuration
const GEMINI_API_KEY = 'AIzaSyALbBapxfuLrMN4IXJFvPekMNGvNKgSw-E';
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:generateContent';

// Store conversation history separately to maintain context
const useConversationHistory = () => {
  const [history, setHistory] = useState([]);
  
  const addToHistory = (message, sender) => {
    setHistory(prev => [...prev, { text: message, sender, timestamp: new Date() }]);
  };
  
  const getConversationContext = () => {
    // Get all messages from the conversation
    const allMessages = [...history].sort((a, b) => a.timestamp - b.timestamp);
    
    // Get all user messages with their numbers
    const allUserMessages = getQuestionHistory();
    const messageMap = new Map();
    allUserMessages.forEach(msg => {
      messageMap.set(msg.text, msg.messageNumber);
    });
    
    // Format the complete conversation context with message numbers
    return allMessages
      .map(msg => {
        const prefix = msg.sender === 'user' ? 'User' : 'AI';
        const messageNumber = messageMap.get(msg.text);
        const numberLabel = msg.sender === 'user' && messageNumber ? `[Message #${messageNumber}] ` : '';
        return `${prefix}: ${numberLabel}${msg.text}`;
      })
      .join('\n\n');
  };
  
  const getQuestionHistory = () => {
    // Track all user messages
    const allUserMessages = [];
    
    // Process messages in chronological order (oldest first)
    const sortedHistory = [...history].sort((a, b) => a.timestamp - b.timestamp);
    
    // Keep track of message indices for reference
    let messageIndex = 0;
    
    for (const msg of sortedHistory) {
      if (msg.sender === 'user') {
        const lowerText = msg.text.toLowerCase().trim();
        
        // Skip very short messages, commands, and meta-questions
        if (lowerText.length > 2 && 
            !lowerText.startsWith('/') &&
            !lowerText.includes('what was my') &&
            !lowerText.includes('what is the') &&
            !/\d+(?:st|nd|rd|th) (?:question|message)/i.test(lowerText)) {
          
          const isQuestion = lowerText.endsWith('?') || 
                           ['what', 'why', 'when', 'where', 'how', 'can you', 'could you', 'would you', 'is there', 'are there', 'do you', 'did you', 'have you', 'has anyone', 'will you']
                             .some(word => lowerText.startsWith(word) || lowerText.includes(' ' + word));
          
          allUserMessages.push({
            text: msg.text,
            timestamp: msg.timestamp,
            isQuestion,
            messageNumber: ++messageIndex  // Assign sequential message number
          });
        }
      }
    }
    
    return allUserMessages;
  };
  
  return { history, addToHistory, getConversationContext, getQuestionHistory };
};

const App = () => {
  const [messages, setMessages] = useState([
    { text: "Hello! I'm CJ AI powered by Google Gemini. How can I help you today?", sender: 'ai' }
  ]);
  const { addToHistory, getConversationContext, getQuestionHistory } = useConversationHistory();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Handle custom responses
  const getAIResponse = (lowerMessage) => {
    // Remove all non-alphanumeric characters and normalize spaces
    const cleanMessage = lowerMessage.replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ');
    
    // Get all user questions from conversation history
    const userQuestions = getQuestionHistory();

    // Handle questions about specific question numbers
    const questionNumberMatch = cleanMessage.match(/(\d+)(?:st|nd|rd|th) question/i) || 
                              cleanMessage.match(/question (\d+)/i);
    
    if (questionNumberMatch) {
      const questionNumber = parseInt(questionNumberMatch[1]);
      if (questionNumber > 0 && questionNumber <= userQuestions.length) {
        const question = userQuestions[questionNumber - 1];
        // Add debug info
        console.log('Question history:', userQuestions.map((q, i) => `${i + 1}. ${q.text}`));
        return `Your ${questionNumber}${getNumberSuffix(questionNumber)} question was: "${question.text}"`;
      } else {
        return `You've only asked ${userQuestions.length} question${userQuestions.length !== 1 ? 's' : ''} so far.`;
      }
    }

    // Handle first question query
    if (cleanMessage.includes('what was my first question') || 
        cleanMessage.includes('what is the first question i ask') ||
        cleanMessage === 'what is my first question') {
      if (userQuestions.length > 0) {
        return `The first question you asked was: "${userQuestions[0].text}"`;
      } else {
        return "You haven't asked any questions yet.";
      }
    }

    // Handle last question query
    if (cleanMessage.includes('what was my last question') || 
        cleanMessage.includes('what is the last question i ask') ||
        cleanMessage === 'what is my last question') {
      if (userQuestions.length > 0) {
        return `The last question you asked was: "${userQuestions[userQuestions.length - 1].text}"`;
      } else {
        return "You haven't asked any questions yet.";
      }
    }
    // Handle creation date questions - check these first
    const creationQuestions = [
      'when were you created',
      'when did he create',
      'when were you made',
      'when did you start',
      'when is your birthday',
      'how old are you',
      'when was cj created',
      'when was cj made',
      'when did cj create',
      'when did cj make',
      'creation date',
      'birth date',
      'birthdate',
      'when cj create you',
      'when cj made you'
    ];

    // Handle creator information
    const creatorQuestions = [
      'who created you',
      'who made you',
      'who is your creator',
      'who is your maker',
      'who is cj',
      'who is charles',
      'who is charles john',
      'tell me about your creator'
    ];

    // Handle nickname questions
    const nicknameQuestions = [
      'what is the nickname',
      'what is the nick name',
      'what do you call',
      'nickname of charles',
      'nickname of charles john duot',
      'nick name of your creator',
      'nick name of your master',
      'nickname of your creator',
      'nickname of your master',
      'what is cj short for',
      'what does cj stand for'
    ];

    // Check nickname questions first
    const isNicknameQuestion = nicknameQuestions.some(q => 
      cleanMessage.includes(q) || 
      cleanMessage === q ||
      cleanMessage.startsWith(q) ||
      cleanMessage.endsWith(q)
    );

    if (isNicknameQuestion) {
      return "His nickname is CJ. It's short for Charles John.";
    }

    // Handle general identity questions
    const identityQuestions = [
      'who are you',
      'what are you',
      'your name',
      'introduce yourself'
    ];

    // Check creation questions first with more precise matching
    const isCreationQuestion = creationQuestions.some(q => 
      cleanMessage.includes(q) || 
      cleanMessage === q ||
      cleanMessage.startsWith(q) ||
      cleanMessage.endsWith(q)
    );

    if (isCreationQuestion) {
      return "I was created on October 1, 2025.";
    }
    
    // Then check creator questions
    if (creatorQuestions.some(q => cleanMessage.includes(q))) {
      return "My creator is **Charles John Duot**. He is a 24-year-old Information Technology graduate from Colegio de Montalban in Rizal, Philippines. He lives in Rodriguez, Rizal, Philippines. He has one sister and was raised by his mother after his father passed away when he was 2 years old.";
    }
    
    // Finally, check general identity questions
    if (identityQuestions.some(q => lowerMessage.includes(q))) {
      return "I'm CJ, an AI assistant powered by Google's Gemini. I'm here to help you with any questions or tasks you might have.";
    }

    return null;
  };

  const fetchAIResponse = async (message, context = '') => {
    const apiKey = GEMINI_API_KEY;
    const url = `${GEMINI_API_URL}?key=${apiKey}`;

    const requestBody = {
      contents: [{
        parts: [{
          text: `You are CJ, a helpful AI assistant. You are having a conversation with a user. 
          Maintain complete context of the entire conversation history and respond naturally.
          
          Complete conversation history (in chronological order):
          ${context || 'No previous messages'}
          
          Current message to respond to: ${message}
          
          Important: 
          - You have access to the complete conversation history
          - When referring to previous messages, use the [Message #X] format to reference specific messages
          - Maintain context of all previous interactions
          - Keep track of the conversation flow and refer back to earlier messages when relevant
          
          CJ:`
        }]
      }],
      generationConfig: {
        maxOutputTokens: 1000,
        temperature: 0.9,
        topP: 0.8,
        topK: 40
      }
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      return data.candidates?.[0]?.content?.parts?.[0]?.text || "I'm sorry, I couldn't process that request.";
    } catch (error) {
      console.error('Error calling AI API:', error);
      return "I'm sorry, I'm having trouble connecting to the AI service. Please try again later.";
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const userInput = input.trim();
    if (!userInput) return;

    const userMessage = { text: userInput, sender: 'user' };
    // Add user message to both UI and conversation history
    setMessages(prev => [...prev, userMessage]);
    addToHistory(userInput, 'user');
    setInput('');
    setIsLoading(true);

    try {
      // Check for custom responses first with more thorough matching
      const lowerInput = userInput.toLowerCase();
      const customResponse = getAIResponse(lowerInput, messages);
      
      if (customResponse) {
        // Add AI response after a small delay for better UX
        setTimeout(() => {
          setMessages(prev => [...prev, { text: customResponse, sender: 'ai' }]);
          addToHistory(customResponse, 'ai');
          setIsLoading(false);
        }, 300);
        return;
      }

      // Get complete conversation context
      const conversationContext = getConversationContext(); // Get all messages for full context
      
      // Get AI response with full context
      const response = await fetchAIResponse(userInput, conversationContext);
      
      // Add AI response to chat
      const aiMessage = { text: response, sender: 'ai' };
      setMessages(prev => [...prev, aiMessage]);
      addToHistory(response, 'ai');
      
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = { 
        text: 'Sorry, I encountered an error. Please try again later.', 
        sender: 'ai' 
      };
      setMessages(prev => [...prev, errorMessage]);
      addToHistory(errorMessage.text, 'ai');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to get number suffix (1st, 2nd, 3rd, 4th, etc.)
  const getNumberSuffix = (number) => {
    const lastDigit = number % 10;
    const lastTwoDigits = number % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
      return 'th';
    }
    
    switch (lastDigit) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  
  // Return the App component
  return (
    <div className="app">
      <header className="app-header">
        <AppLogo />
        <h1>AI Assistant</h1>
      </header>
      
      <div className="messages">
        {messages.map((msg, index) => (
          <MessageWrapper key={index} $sender={msg.sender}>
            {msg.sender === 'ai' && <AppLogo />}
            <MessageContainer $sender={msg.sender}>
              <div className="message-content">
                {msg.text.split('\n').map((line, i) => (
                  <p key={i} style={{ margin: '4px 0' }}>{line}</p>
                ))}
              </div>
            </MessageContainer>
          </MessageWrapper>
        ))}
      </div>

      <form onSubmit={handleSend} className="chat-container">
        <div className="input-area">
          <input
            id="message-input"
            name="message"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your message..."
            disabled={isLoading}
            style={{ 
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)'
            }}
          />
          <button 
            type="submit" 
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : 'Send'}
          </button>
        </div>
      </form>

    </div>
  );
};

export default App;
